// The FILE TABLE (N-X-3), recreated 1:1 from the prototype's artifact table (app.css .ftab/.ftab-row: the
// mono column heads Name/Kind/Origin/Updated, the kind-tinted badge, the name + mono ext sub-line, the kind
// pill, the origin label, the compact updated label). Each row is the OWNING module's (I) composed listing
// row: the File name links to the module's file-serve URL honouring its DENY-BY-DEFAULT decision — an
// `inline` File opens in a tab; a `download`-only File (script-capable / type-mismatched — G-031) only
// downloads, never inline-opens. The name is the module's SANITISED displayName (G-032); the origin is a
// composed label, never a raw id (G-197). Artifact history/manifest and its independently verified Task link
// render as siblings below the file link, so interactive anchors are never nested. No parallel serving path.

import { Icon, type IconName } from '../../app/Icon.tsx';
import { Panel, EmptyState } from '../primitives.tsx';
import type { FileArtifactProjection, FileSurfaceKind, FileViewRow } from '../know-projections.ts';

/** The platform's file-serve URL for a File id — the ONLY byte path (no parallel serving in the client).
 *  The route serves the owning module's deny-by-default decision verbatim (IFC-ART-003; deep-sweep
 *  2026-07-16 N2 registered it — the old `/artifacts/files/` path never had a serving route behind it). */
function fileServeUrl(fileId: string): string {
  return `/dashboard/api/files/${encodeURIComponent(fileId)}/content`;
}

/** The badge icon + tint class per surface kind (prototype badge map). */
const KIND_BADGE: Readonly<Record<FileSurfaceKind, { icon: IconName; cls: string }>> = {
  report: { icon: 'files', cls: 'ft-badge k-report' },
  database: { icon: 'db', cls: 'ft-badge k-db' },
  upload: { icon: 'upload', cls: 'ft-badge k-upload' },
};

/** The kind pill label + tone per surface kind (prototype pill map). */
const KIND_PILL: Readonly<Record<FileSurfaceKind, { label: string; tone: string }>> = {
  report: { label: 'Report', tone: 'pill go' },
  database: { label: 'Database', tone: 'pill db' },
  upload: { label: 'Upload', tone: 'pill pending' },
};

/** The mono ext sub-line: a living dataset shows its liveness + version; a file its dot-extension. */
export function extLabel(row: FileViewRow): string {
  if (row.kind === 'database') {
    if (row.artifact?.state === 'superseded') return `prior · v${String(row.artifact.version)}`;
    return row.version !== null ? `live · v${String(row.version)}` : 'live';
  }
  const dot = row.displayName.lastIndexOf('.');
  if (dot > 0 && dot < row.displayName.length - 1) return row.displayName.slice(dot);
  const slash = row.contentType.indexOf('/');
  return slash >= 0 ? `.${row.contentType.slice(slash + 1)}` : row.contentType;
}

function taskDeepLink(taskId: string): string {
  return `#tasks/${encodeURIComponent(taskId)}`;
}

function ArtifactDetails({ row }: { readonly row: FileViewRow }): React.JSX.Element | null {
  const artifact = row.artifact;
  if (artifact === null) return null;
  return (
    <div
      className="ft-artifact"
      data-artifact={artifact.id}
      data-artifact-version={artifact.version}
      data-artifact-state={artifact.state}
    >
      <div className="ft-artifact-line">
        <span className={`ft-version ${artifact.state}`}>
          v{artifact.version} · {artifact.state === 'current' ? 'current' : 'prior'}
        </span>
        <span>
          Provenance: <code>{artifact.sourceClass}</code> · {artifact.source}
        </span>
        <span>
          Scope: <span>{artifact.scope}</span>
        </span>
        <span>
          Reliability: <code>{artifact.reliability.evidenceStatus}</code>
        </span>
      </div>
      <ReliabilityDetails artifact={artifact} />
      {artifact.kind === 'report' ? <ReportManifest artifact={artifact} /> : null}
      {row.taskLink === null ? null : (
        <a
          className="ft-task-link"
          data-task-link={row.taskLink.taskId}
          href={taskDeepLink(row.taskLink.taskId)}
        >
          Task: {row.taskLink.title} · {row.taskLink.state}
        </a>
      )}
    </div>
  );
}

function ReliabilityDetails({
  artifact,
}: {
  readonly artifact: FileArtifactProjection;
}): React.JSX.Element | null {
  const { limitations, freshnessGuidance } = artifact.reliability;
  if (limitations === undefined && freshnessGuidance === undefined) return null;
  return (
    <div className="ft-reliability">
      {limitations === undefined ? null : <span>{limitations}</span>}
      {freshnessGuidance === undefined ? null : <span>{freshnessGuidance}</span>}
    </div>
  );
}

function ReportManifest({
  artifact,
}: {
  readonly artifact: FileArtifactProjection;
}): React.JSX.Element {
  const manifest = artifact.reportManifest;
  if (manifest === null) {
    return <div className="ft-manifest legacy">Legacy report · no manifest recorded</div>;
  }
  return (
    <div className="ft-manifest" data-report-manifest>
      <span>Sections: {manifest.sectionIds.join(', ')}</span>
      <span>
        Metrics: {manifest.metricRefs.length === 0 ? 'none' : manifest.metricRefs.join(', ')}
      </span>
      <span>
        Sources: {manifest.sourceRefs.length === 0 ? 'none' : manifest.sourceRefs.join(', ')}
      </span>
      <span>
        Remembered context — not evidence:{' '}
        {manifest.memoryContextRefs.length === 0
          ? 'none'
          : manifest.memoryContextRefs.map((ref) => `${ref.title} (${ref.path})`).join(', ')}
      </span>
    </div>
  );
}

/** A compact "updated" label from the recorded timestamp: today → HH:MM; this week → weekday;
 *  this year → "1 Jul"; older → "Apr 2025". Display-only formatting of the recorded instant. */
export function whenLabel(createdAt: string, now: Date = new Date()): string {
  const then = new Date(createdAt);
  if (Number.isNaN(then.getTime())) return createdAt.slice(0, 10);
  const dayMs = 24 * 60 * 60 * 1000;
  const sameDay = then.toDateString() === now.toDateString();
  if (sameDay) {
    return then.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  if (now.getTime() - then.getTime() < 7 * dayMs) {
    return then.toLocaleDateString('en-GB', { weekday: 'short' });
  }
  if (then.getFullYear() === now.getFullYear()) {
    return then.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }
  return then.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

/** The display name without its extension (the ext renders on the mono sub-line, prototype .ft-nm). */
function bareName(row: FileViewRow): string {
  if (row.kind === 'database') return row.displayName.replace(/\.[a-z0-9]+$/i, '');
  const dot = row.displayName.lastIndexOf('.');
  return dot > 0 ? row.displayName.slice(0, dot) : row.displayName;
}

/** The table's mono column-head row (prototype .ftab-row.head). */
function HeadRow(): React.JSX.Element {
  return (
    <div className="ftab-row head">
      <span className="mono-label">Name</span>
      <span className="mono-label">Kind</span>
      <span className="mono-label ft-origin-h">Origin</span>
      <span className="mono-label ft-when-h" style={{ textAlign: 'right' }}>
        Updated
      </span>
    </div>
  );
}

/** One File row — file and Task navigation are sibling anchors, never nested interactive elements. */
function FileRow({ row }: { readonly row: FileViewRow }): React.JSX.Element {
  const inline = row.disposition === 'inline';
  const badge = KIND_BADGE[row.kind];
  const pill = KIND_PILL[row.kind];
  return (
    <article
      className="ftab-entry"
      data-file={row.fileId}
      data-kind={row.kind}
      data-disposition={row.disposition}
      data-evidence-status={row.evidenceStatus ?? 'unregistered'}
    >
      <div className="ftab-row">
        <a
          className="ft-name ft-file-link"
          href={fileServeUrl(row.fileId)}
          {...(inline ? { target: '_blank', rel: 'noreferrer' } : { download: row.displayName })}
          aria-label={`${inline ? 'View' : 'Download'}: ${row.displayName}`}
        >
          <span className={badge.cls}>
            <Icon name={badge.icon} size={16} />
          </span>
          <span className="ft-nm">
            <span className="nm">{bareName(row)}</span>
            <span className="ext">{extLabel(row)}</span>
          </span>
        </a>
        <span className="ft-kind">
          <span className={pill.tone}>
            <span className="dot" aria-hidden="true" /> {KIND_PILL[row.kind].label}
          </span>
        </span>
        <span className="ft-origin">{row.originLabel}</span>
        <span className="ft-when">{whenLabel(row.createdAt)}</span>
      </div>
      <ArtifactDetails row={row} />
    </article>
  );
}

export interface FileTableProps {
  readonly rows: readonly FileViewRow[];
}

/** The Files table — one row per composed listing row, honouring the module's deny-by-default decision. */
export function FileTable({ rows }: FileTableProps): React.JSX.Element {
  if (rows.length === 0) {
    return (
      <Panel>
        <EmptyState>No files here yet.</EmptyState>
      </Panel>
    );
  }
  return (
    <div className="ftab panel" data-file-table>
      <HeadRow />
      {rows.map((row) => (
        <FileRow key={row.fileId} row={row} />
      ))}
    </div>
  );
}
